import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { ArticleAutocomplete } from '../../components/ui/article-autocomplete';
import {
  Package, FileText, MapPin, CheckCircle, Camera, Edit, Trash2,
  Plus, ArrowRight, ArrowLeft, Save, AlertCircle, DollarSign, Calendar as CalendarIcon, Loader2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { SupplierAutocomplete } from '../../components/ui/supplier-autocomplete';
import { Combobox } from '../../components/ui/combobox';

const GoodsReceiptWizard = ({ open, onClose, onSuccess, editReceipt }) => {
  const { plants } = useData();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [bins, setBins] = useState([]);
  const [freshArticles, setFreshArticles] = useState([]);
  
  const [receiptData, setReceiptData] = useState({
    // Step 1: Invoice info
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    total_invoice_value: 0,
    currency: 'RON',
    exchange_rate: 1.0,
    plant_id: '',
    notes: '',
    // Step 2: Items
    items: [],
    // Step 3: Documentation
    photos: [],
    signature: null
  });

  const [currentItem, setCurrentItem] = useState({
    article: null,
    quantity: '',
    unit_price: 0,
    discount_percent: 0,
    lot_number: '',
    expiry_date: '',
    qc_status: 'ok',
    bin_allocations: []
  });

  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [lastPriceInfo, setLastPriceInfo] = useState(null);
  const [priceVarianceSettings, setPriceVarianceSettings] = useState({ threshold_percent: 5, enabled: true });
  const [articlesReady, setArticlesReady] = useState(false);

  useEffect(() => {
    if (open) {
      setArticlesReady(false);
      setFreshArticles([]);
      fetchSuppliers();
      // Fetch articles directly via API to guarantee fresh data
      api.get('/articles').then(response => {
        setFreshArticles(response.data || []);
        setArticlesReady(true);
      }).catch(() => {
        setArticlesReady(true);
      });
      fetchBins();
      // Fetch price variance settings
      api.get('/supplier-intelligence/settings/price-variance').then(r => setPriceVarianceSettings(r.data)).catch(() => {});
      // Populate form if editing
      if (editReceipt) {
        setReceiptData({
          invoice_number: editReceipt.invoice_number || '',
          invoice_date: editReceipt.invoice_date ? new Date(editReceipt.invoice_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          supplier_id: editReceipt.supplier_id || '',
          total_invoice_value: editReceipt.total_invoice_value || 0,
          currency: editReceipt.currency || 'RON',
          exchange_rate: editReceipt.exchange_rate || 1.0,
          plant_id: editReceipt.plant_id || '',
          notes: editReceipt.notes || '',
          items: (editReceipt.items || []).map(item => ({
            ...item,
            id: item.id || `temp-${Date.now()}-${Math.random()}`,
            unit_price: item.unit_price || 0,
            discount_percent: item.discount_percent || 0,
            total_price: item.total_price || 0,
            bin_allocations: item.bin_allocations || [],
          })),
          photos: editReceipt.photos || [],
          signature: editReceipt.signature || null
        });
        setStep(1);
      }
    }
  }, [open, editReceipt]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  // Fetch last price when article + supplier are selected
  useEffect(() => {
    if (currentItem.article?.id && receiptData.supplier_id && showItemDialog) {
      api.get(`/supplier-intelligence/last-price/${currentItem.article.id}/${receiptData.supplier_id}`)
        .then(r => setLastPriceInfo(r.data))
        .catch(() => setLastPriceInfo(null));
    } else {
      setLastPriceInfo(null);
    }
  }, [currentItem.article?.id, receiptData.supplier_id, showItemDialog]);

  // Filter articles to show only those that have the selected supplier
  const filteredArticles = useMemo(() => {
    if (!receiptData.supplier_id) {
      return freshArticles; // If no supplier selected, show all
    }
    return freshArticles.filter(article => 
      article.suppliers && 
      article.suppliers.some(s => s.supplier_id === receiptData.supplier_id)
    );
  }, [freshArticles, receiptData.supplier_id]);

  const fetchBins = async () => {
    try {
      const response = await api.get('/bins');
      setBins(response.data);
    } catch (error) {
      console.error('Error fetching bins:', error);
    }
  };

  const addItem = () => {
    if (!currentItem.article) {
      toast.error('Selecteaza un articol');
      return;
    }
    if (!currentItem.quantity || currentItem.quantity <= 0) {
      toast.error('Introduceti cantitatea');
      return;
    }
    if (currentItem.bin_allocations.length === 0) {
      toast.error('Aloca cel putin un BIN');
      return;
    }

    const totalAllocated = currentItem.bin_allocations.reduce((sum, a) => sum + a.quantity, 0);
    if (totalAllocated !== currentItem.quantity) {
      toast.error(`Cantitate alocata (${totalAllocated}) difera de cantitate primita (${currentItem.quantity})`);
      return;
    }

    const item = {
      id: editingItemIndex !== null ? receiptData.items[editingItemIndex].id : `temp-${Date.now()}`,
      article_id: currentItem.article.id,
      article_name: currentItem.article.name,
      article_code: currentItem.article.code,
      quantity: currentItem.quantity,
      unit: currentItem.article.unit || 'buc',
      unit_price: currentItem.unit_price,
      discount_percent: currentItem.discount_percent,
      total_price: currentItem.quantity * currentItem.unit_price * (1 - currentItem.discount_percent / 100),
      lot_number: currentItem.lot_number,
      expiry_date: currentItem.expiry_date,
      qc_status: currentItem.qc_status,
      bin_allocations: currentItem.bin_allocations
    };

    if (editingItemIndex !== null) {
      const newItems = [...receiptData.items];
      newItems[editingItemIndex] = item;
      setReceiptData({ ...receiptData, items: newItems });
    } else {
      setReceiptData({ ...receiptData, items: [...receiptData.items, item] });
    }

    resetCurrentItem();
    setShowItemDialog(false);
    setEditingItemIndex(null);
  };

  const resetCurrentItem = () => {
    setCurrentItem({
      article: null,
      quantity: '',
      unit_price: 0,
      discount_percent: 0,
      lot_number: '',
      expiry_date: '',
      qc_status: 'ok',
      bin_allocations: []
    });
  };

  const removeItem = (index) => {
    setReceiptData({
      ...receiptData,
      items: receiptData.items.filter((_, i) => i !== index)
    });
  };

  const editItem = (index) => {
    const item = receiptData.items[index];
    setCurrentItem({
      article: { id: item.article_id, name: item.article_name, code: item.article_code, unit: item.unit },
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_percent: item.discount_percent,
      lot_number: item.lot_number || '',
      expiry_date: item.expiry_date || '',
      qc_status: item.qc_status || 'ok',
      bin_allocations: item.bin_allocations || []
    });
    setEditingItemIndex(index);
    setShowItemDialog(true);
  };

  const addBinAllocation = () => {
    const allocated = currentItem.bin_allocations.reduce((sum, a) => sum + a.quantity, 0);
    const remaining = currentItem.quantity - allocated;
    
    if (remaining <= 0) {
      toast.warning('Cantitate deja alocata complet');
      return;
    }

    setCurrentItem({
      ...currentItem,
      bin_allocations: [
        ...currentItem.bin_allocations,
        { bin_id: '', bin_code: '', cabinet: '', shelf: '', position: '', quantity: remaining }
      ]
    });
  };

  const updateBinAllocation = (index, field, value) => {
    const newAllocations = [...currentItem.bin_allocations];
    
    if (field === 'bin_id') {
      const bin = bins.find(b => b.id === value);
      if (bin) {
        newAllocations[index] = {
          ...newAllocations[index],
          bin_id: bin.id,
          bin_code: bin.code,
          cabinet: bin.cabinet,
          shelf: bin.shelf,
          position: bin.position
        };
        // Auto-fill plant_id from bin if not already set
        if (bin.plant_id && !receiptData.plant_id) {
          setReceiptData(prev => ({ ...prev, plant_id: bin.plant_id }));
          toast.info('Sediu auto-completat din locația BIN', { duration: 3000 });
        }
      }
    } else {
      newAllocations[index][field] = field === 'quantity' ? parseInt(value) || 0 : value;
    }
    
    setCurrentItem({ ...currentItem, bin_allocations: newAllocations });
  };

  const removeBinAllocation = (index) => {
    setCurrentItem({
      ...currentItem,
      bin_allocations: currentItem.bin_allocations.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Clean items: convert empty date strings to null for Pydantic
      const cleanedItems = receiptData.items.map(item => ({
        ...item,
        expiry_date: item.expiry_date || null,
        manufacturing_date: item.manufacturing_date || null,
        lot_number: item.lot_number || null,
        batch_number: item.batch_number || null,
      }));
      
      const payload = {
        ...receiptData,
        items: cleanedItems,
        receipt_type: 'invoice',
        received_by: user.id
      };
      
      if (editReceipt) {
        await api.put(`/wms/receipts/${editReceipt.id}`, payload);
        toast.success('Receptie actualizata cu succes!');
      } else {
        const response = await api.post('/wms/receipts', payload);
        toast.success(`Receptie ${response.data.receipt_number} creata cu succes!`);
      }
      onSuccess?.();
      onClose?.();
      resetWizard();
    } catch (error) {
      console.error('Receipt error:', error);
      toast.error(error.response?.data?.detail || 'Eroare la salvare receptie');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setReceiptData({
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      supplier_id: '',
      total_invoice_value: 0,
      currency: 'RON',
      exchange_rate: 1.0,
      plant_id: '',
      notes: '',
      items: [],
      photos: [],
      signature: null
    });
  };

  const canProceed = () => {
    switch(step) {
      case 1:
        // plant_id is now optional - will be auto-filled from first BIN allocation
        return receiptData.invoice_number && receiptData.supplier_id;
      case 2:
        return receiptData.items.length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const totalValue = receiptData.items.reduce((sum, item) => sum + item.total_price, 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            {editReceipt ? 'Editare' : 'Receptie Marfa cu Factura'} - Pas {step}/3
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1">
              <div className={`h-2 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-slate-200'}`} />
              <p className={`text-xs mt-1 text-center font-semibold ${s <= step ? 'text-blue-600' : 'text-slate-400'}`}>
                {s === 1 ? 'Factura' : s === 2 ? 'Articole' : 'Confirmare'}
              </p>
            </div>
          ))}
        </div>

        {/* Step 1: Invoice Info */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Informatii Factura</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nr. Factura *</Label>
                <Input
                  value={receiptData.invoice_number}
                  onChange={(e) => setReceiptData({...receiptData, invoice_number: e.target.value})}
                  placeholder="ex: FV-2025-001"
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Data Factura *</Label>
                <Input
                  type="date"
                  value={receiptData.invoice_date}
                  onChange={(e) => setReceiptData({...receiptData, invoice_date: e.target.value})}
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Furnizor *</Label>
                <SupplierAutocomplete
                  suppliers={suppliers}
                  value={receiptData.supplier_id}
                  onSelect={(supplier) => setReceiptData({...receiptData, supplier_id: supplier ? supplier.id : ''})}
                  placeholder="Cauta furnizor..."
                  triggerClassName="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Sediu <span className="text-slate-400 text-xs font-normal">(auto din BIN)</span></Label>
                <Select
                  value={receiptData.plant_id}
                  onValueChange={(v) => setReceiptData({...receiptData, plant_id: v})}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Se va completa automat din BIN" />
                  </SelectTrigger>
                  <SelectContent>
                    {plants.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select
                  value={receiptData.currency}
                  onValueChange={(v) => setReceiptData({...receiptData, currency: v})}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RON">RON</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {receiptData.currency !== 'RON' && (
                <div className="space-y-2">
                  <Label>Curs Valutar</Label>
                  <Input
                    type="number"
                    value={receiptData.exchange_rate}
                    onChange={(e) => setReceiptData({...receiptData, exchange_rate: parseFloat(e.target.value)})}
                    step="0.0001"
                    className="h-12"
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={receiptData.notes}
                onChange={(e) => setReceiptData({...receiptData, notes: e.target.value})}
                placeholder="Observatii despre receptie..."
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Step 2: Items */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-lg font-bold text-slate-900">Articole Primite</h3>
              <Button onClick={() => { resetCurrentItem(); setShowItemDialog(true); }} className="h-10">
                <Plus className="w-4 h-4 mr-2" />
                Adauga Articol
              </Button>
            </div>

            {receiptData.items.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">Niciun articol adaugat</p>
                  <p className="text-sm text-slate-500 mt-1">Click &ldquo;Adauga Articol&rdquo; pentru a incepe</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {receiptData.items.map((item, index) => (
                  <Card key={item.id} className="border-2">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-bold text-lg">{item.article_name}</p>
                            <Badge variant="outline">{item.article_code}</Badge>
                            {item.qc_status === 'damaged' && (
                              <Badge className="bg-red-600">Damaged</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500">Cantitate</p>
                              <p className="font-semibold">{item.quantity} {item.unit}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Pret unitar</p>
                              <p className="font-semibold">{item.unit_price.toFixed(2)} {receiptData.currency}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Discount</p>
                              <p className="font-semibold">{item.discount_percent}%</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Total</p>
                              <p className="font-semibold text-green-600">{item.total_price.toFixed(2)} {receiptData.currency}</p>
                            </div>
                          </div>
                          {item.lot_number && (
                            <p className="text-xs text-slate-600 mt-2">LOT: {item.lot_number}</p>
                          )}
                          <div className="mt-2">
                            <p className="text-xs text-slate-600 mb-1">Alocare BIN ({item.bin_allocations.length}):</p>
                            <div className="flex flex-wrap gap-1">
                              {item.bin_allocations.map((alloc, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {alloc.cabinet}→{alloc.shelf}→{alloc.position}: {alloc.quantity}{item.unit}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => editItem(index)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <p className="font-semibold">TOTAL RECEPTIE:</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {totalValue.toFixed(2)} {receiptData.currency}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Preview si Confirmare</h3>
            
            {/* Invoice Summary */}
            <Card className="bg-blue-50">
              <CardContent className="p-4 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-600">Factura:</span> <span className="font-semibold">{receiptData.invoice_number}</span></div>
                  <div><span className="text-slate-600">Data:</span> <span className="font-semibold">{receiptData.invoice_date}</span></div>
                  <div><span className="text-slate-600">Furnizor:</span> <span className="font-semibold">{suppliers.find(s => s.id === receiptData.supplier_id)?.name}</span></div>
                  <div><span className="text-slate-600">Sediu:</span> <span className="font-semibold">{plants.find(p => p.id === receiptData.plant_id)?.name}</span></div>
                </div>
              </CardContent>
            </Card>
            
            {/* Items Summary */}
            <div>
              <p className="font-semibold mb-2">{receiptData.items.length} Articole:</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {receiptData.items.map((item, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-3">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">{item.article_name}</p>
                          <p className="text-sm text-slate-600">{item.quantity} {item.unit} × {item.unit_price} {receiptData.currency}</p>
                        </div>
                        <p className="font-bold">{item.total_price.toFixed(2)} {receiptData.currency}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {/* Final Total */}
            <Card className="bg-green-50 border-2 border-green-400">
              <CardContent className="p-5 text-center">
                <p className="text-slate-700 mb-1">Valoare Totala Receptie</p>
                <p className="text-4xl font-black text-green-600">{totalValue.toFixed(2)} {receiptData.currency}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Footer Navigation */}
        <DialogFooter className="flex justify-between mt-6">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Inapoi
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { onClose(); resetWizard(); }}>
              Anuleaza
            </Button>
            {step < 3 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-blue-600">
                Urmatorul Pas
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="bg-green-600">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvare...' : editReceipt ? 'Actualizeaza Receptia' : 'Confirma Receptia'}
              </Button>
            )}
          </div>
        </DialogFooter>

        {/* Item Dialog */}
        <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItemIndex !== null ? 'Editeaza' : 'Adauga'} Articol</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Articol *</Label>
                {!articlesReady && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                    <p className="text-sm text-blue-800">
                      <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />
                      Se incarca articolele...
                    </p>
                  </div>
                )}
                {receiptData.supplier_id && articlesReady && filteredArticles.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                    <p className="text-sm text-amber-800">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Niciun articol configurat pentru acest furnizor. 
                      Adaugati furnizorul in fisa articolului din <strong>Articole / Consumabile</strong>.
                    </p>
                  </div>
                )}
                <ArticleAutocomplete
                  articles={filteredArticles}
                  supplierId={receiptData.supplier_id}
                  onSelect={(article) => {
                    if (!article) {
                      setCurrentItem({...currentItem, article: null});
                      return;
                    }
                    
                    // Auto-fill BIN if article has default_bin_id
                    if (article.default_bin_id && article.default_bin_location) {
                      const parts = article.default_bin_location.split(' → ');
                      const cabinet = parts[0] || '';
                      const shelf = parts[1] || '';
                      const position = parts[2] || '';
                      setCurrentItem({
                        ...currentItem,
                        article: article,
                        bin_allocations: [{
                          bin_id: article.default_bin_id,
                          bin_code: article.default_bin_code || '',
                          cabinet: cabinet.trim(),
                          shelf: shelf.trim(),
                          position: position.trim(),
                          quantity: currentItem.quantity || 0
                        }]
                      });
                      toast.info(`BIN preferat auto-completat: ${article.default_bin_code || article.default_bin_location}`, { duration: 4000 });
                      
                      // Auto-fill plant_id from article's default bin
                      const defaultBin = bins.find(b => b.id === article.default_bin_id);
                      if (defaultBin?.plant_id && !receiptData.plant_id) {
                        setReceiptData(prev => ({ ...prev, plant_id: defaultBin.plant_id }));
                        toast.info('Sediu auto-completat din BIN-ul preferat', { duration: 3000 });
                      }
                    } else {
                      setCurrentItem({...currentItem, article: article});
                    }
                  }}
                  placeholder="Cauta articol..."
                />
                {currentItem.article && (
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="font-semibold">{currentItem.article.name}</p>
                    <p className="text-sm text-slate-600">{currentItem.article.code}</p>
                    {/* Show supplier-specific article name if mapped */}
                    {receiptData.supplier_id && currentItem.article.suppliers?.find(s => s.supplier_id === receiptData.supplier_id) && (
                      <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200" data-testid="supplier-article-mapping">
                        <p className="text-xs text-amber-700 font-medium">Denumire la furnizor:</p>
                        <p className="text-sm font-semibold text-amber-900">
                          {currentItem.article.suppliers.find(s => s.supplier_id === receiptData.supplier_id)?.supplier_article_name || 'Neconfigurata'}
                        </p>
                        {currentItem.article.suppliers.find(s => s.supplier_id === receiptData.supplier_id)?.quality_grade && (
                          <p className="text-xs text-amber-600 mt-1">
                            Calitate: {currentItem.article.suppliers.find(s => s.supplier_id === receiptData.supplier_id).quality_grade}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cantitate Primita *</Label>
                  <Input
                    type="number"
                    value={currentItem.quantity}
                    onChange={(e) => {
                      const newQty = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      // Auto-update first BIN allocation quantity when there's only one allocation
                      if (currentItem.bin_allocations.length === 1) {
                        setCurrentItem({
                          ...currentItem, 
                          quantity: newQty,
                          bin_allocations: [{
                            ...currentItem.bin_allocations[0],
                            quantity: newQty
                          }]
                        });
                      } else if (currentItem.bin_allocations.length === 0) {
                        // If no BIN allocations, just update quantity
                        setCurrentItem({...currentItem, quantity: newQty});
                      } else {
                        // Multiple allocations - just update quantity, user must manually adjust
                        setCurrentItem({...currentItem, quantity: newQty});
                      }
                    }}
                    min="1"
                    step="1"
                    placeholder="Introduceti cantitatea"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pret Unitar *</Label>
                  <Input
                    type="number"
                    value={currentItem.unit_price}
                    onChange={(e) => setCurrentItem({...currentItem, unit_price: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="0.01"
                    className="h-12"
                  />
                  {/* Price variance alert */}
                  {lastPriceInfo && lastPriceInfo.last_price > 0 && currentItem.unit_price > 0 && (
                    (() => {
                      const diff = currentItem.unit_price - lastPriceInfo.last_price;
                      const pct = ((diff / lastPriceInfo.last_price) * 100);
                      const absPct = Math.abs(pct);
                      const isOver = absPct >= (priceVarianceSettings.threshold_percent || 5);
                      if (absPct < 0.1) return null;
                      return (
                        <div className={`mt-1 p-2 rounded text-xs ${isOver ? (diff > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200') : 'bg-slate-50 border border-slate-200'}`}
                          data-testid="price-variance-alert">
                          <div className="flex items-center gap-1">
                            <AlertCircle className={`w-3 h-3 ${isOver && diff > 0 ? 'text-red-500' : isOver && diff < 0 ? 'text-green-500' : 'text-slate-400'}`} />
                            <span className={`font-medium ${isOver && diff > 0 ? 'text-red-700' : isOver && diff < 0 ? 'text-green-700' : 'text-slate-600'}`}>
                              {diff > 0 ? '+' : ''}{diff.toFixed(2)} RON ({diff > 0 ? '+' : ''}{pct.toFixed(1)}%)
                            </span>
                            <span className="text-slate-500">
                              vs. ultimul pret: {lastPriceInfo.last_price.toFixed(2)} RON
                            </span>
                          </div>
                          {isOver && diff > 0 && (
                            <p className="text-red-600 mt-1 font-medium">Variatie depaseste pragul de {priceVarianceSettings.threshold_percent}%</p>
                          )}
                        </div>
                      );
                    })()
                  )}
                  {lastPriceInfo && lastPriceInfo.last_price > 0 && currentItem.unit_price === 0 && (
                    <p className="text-xs text-slate-500 mt-1">Ultimul pret: {lastPriceInfo.last_price.toFixed(2)} RON</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    value={currentItem.discount_percent}
                    onChange={(e) => setCurrentItem({...currentItem, discount_percent: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="100"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Articol</Label>
                  <Input
                    value={(currentItem.quantity * currentItem.unit_price * (1 - currentItem.discount_percent / 100)).toFixed(2)}
                    disabled
                    className="h-12 bg-green-50 font-bold"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LOT/Batch</Label>
                  <Input
                    value={currentItem.lot_number}
                    onChange={(e) => setCurrentItem({...currentItem, lot_number: e.target.value})}
                    placeholder="ex: LOT-2025-001"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Expirare</Label>
                  <Input
                    type="date"
                    value={currentItem.expiry_date}
                    onChange={(e) => setCurrentItem({...currentItem, expiry_date: e.target.value})}
                    className="h-12"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Quality Control</Label>
                <Select
                  value={currentItem.qc_status}
                  onValueChange={(v) => setCurrentItem({...currentItem, qc_status: v})}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ok">✅ OK - Produs conform</SelectItem>
                    <SelectItem value="damaged">❌ Damaged - Deteriorat</SelectItem>
                    <SelectItem value="to_inspect">⚠️ To Inspect - Necesita verificare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* BIN Allocations */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Alocare Locatii (BIN) *</Label>
                  <Button type="button" size="sm" onClick={addBinAllocation} variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Split pe alt BIN
                  </Button>
                </div>
                
                {currentItem.bin_allocations.map((alloc, index) => (
                  <div key={index} className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-2">
                      <Label className="text-xs">BIN Location</Label>
                      <Select
                        value={alloc.bin_id}
                        onValueChange={(v) => updateBinAllocation(index, 'bin_id', v)}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Selecteaza BIN" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {bins.map(b => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.cabinet} → {b.shelf} → {b.position} ({b.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {alloc.bin_id && currentItem.article?.default_bin_id === alloc.bin_id && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> BIN preferat - auto-completat
                        </p>
                      )}
                      {alloc.cabinet && !(currentItem.article?.default_bin_id === alloc.bin_id) && (
                        <p className="text-xs text-slate-600 mt-1">
                          {alloc.cabinet} → {alloc.shelf} → {alloc.position}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Cantitate</Label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          value={alloc.quantity}
                          onChange={(e) => updateBinAllocation(index, 'quantity', e.target.value)}
                          min="1"
                          step="1"
                          className="h-10"
                        />
                        {currentItem.bin_allocations.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBinAllocation(index)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {currentItem.bin_allocations.length > 0 && (
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-sm">
                      Total alocat: <span className="font-bold">
                        {currentItem.bin_allocations.reduce((sum, a) => sum + a.quantity, 0)} 
                      </span> / {currentItem.quantity}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowItemDialog(false); resetCurrentItem(); }}>
                Anuleaza
              </Button>
              <Button onClick={addItem} className="bg-green-600">
                <Save className="w-4 h-4 mr-2" />
                {editingItemIndex !== null ? 'Actualizeaza' : 'Adauga'} Articol
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default GoodsReceiptWizard;
